<template>
	<Suspense>
		<loadDashboardGeo />
	</Suspense>
	<featureSection :feature-name="'dashboard geo'">
		<main style="margin-bottom: 3rem">
			<div class="title" data-cy="dash_title" style="flex-direction: row; gap: .8rem;">
				<h1>{{ t('dashboard.title') }}</h1>
				<p>{{ compared_dates }}</p>
			</div>

			<div v-sticky="{
				priority: 1,
				shrinkClass: 'filters-shrunk',
				verticalShrink: true,
				shrinkAmount: 0.95
			}" class="filter">
				<div class="flex align-items-center gap-2" v-if="user.hasAccess('project manager')">
					<div :class="{ 'active-view': pageView === 'geo_analysis', 'view-option': true }"
						@click="changePageView('geo_analysis')" data-cy="dash_select_geo_analysis" class="py-2 px-3"
						style="border-radius: 0.5rem">
						{{ t('dashboard.GEO_analysis') }}
					</div>
					<div :class="{ 'active-view': pageView === 'task_management', 'view-option': true }"
						@click="changePageView('task_management')" data-cy="dash_select_task_management"
						class="py-2 px-3" style="border-radius: 0.5rem">
						{{ t('dashboard.team_management') }}
					</div>
				</div>
				<div class="flex justify-content-end align-items-center gap-3" v-if="pageView === 'geo_analysis'">
					<button v-if="selectedLabelIds.length > 0" class="label-details-button"
						@click="labelDetailsOpen = !labelDetailsOpen">
						{{ t('details') }}
					</button>
					<competitorsMenu :show-label-filter="true" />
					<div class="calendar">
						<dashboardGeoCalendar style="width: 250px !important;" />
					</div>
				</div>
			</div>

			<section v-if="pageView === 'geo_analysis' && labelDetailsOpen && selectedLabelIds.length > 0"
				class="label-details-panel">
				<Card style="border-radius: 8px;">
					<h3>{{ t('dashboard_geo.label_questions.title') }}</h3>
					<ul v-if="labelQuestions.length > 0" class="label-details-list">
						<li v-for="question in labelQuestions" :key="question.id">{{ question.question }}</li>
					</ul>
					<p v-else class="no-data">{{ t('no_data') }}</p>
				</Card>
			</section>

			<section v-if="pageView === 'geo_analysis'" class="mt-4">
				<overviewGeo />
			</section>

			<template id="geo_analysis" v-if="pageView === 'geo_analysis'">
				<div class="title">
					<h2>{{ t('dashboard_geo.share_voice.title') }}</h2>
				</div>
				<Card v-skeleton="{ show: loading }" style="min-height: 450px; border-radius: 8px;">
					<graphShareVoice />
				</Card>
			</template>

			<template id="geo_analysis" v-if="pageView === 'geo_analysis'">
				<div class="title">
					<h2>{{ t('dashboard_geo.landscape.title') }}</h2>
					<p>{{ t('dashboard_geo.subtitle', { aiType: aiTypeName }) }}</p>
				</div>
				<Card v-skeleton="{ show: loading }" style="border-radius: 8px;">
					<competitiveGeoAnalysis />
				</Card>
			</template>

			<template id="geo_analysis" v-if="pageView === 'geo_analysis'">
				<div class="title">
					<h2>{{ t('dashboard_geo.brand.title') }}</h2>
					<p>{{ t('dashboard_geo.subtitle', { aiType: aiTypeName }) }}</p>
				</div>
				<Card v-skeleton="{ show: loading }" style="min-height: 610px; border-radius: 8px;">
					<brandPerformance />
				</Card>
			</template>

			<template id="geo_analysis" v-if="pageView === 'geo_analysis'">
				<div class="title">
					<h2>{{ t('dashboard_geo.labels_share_voice.title') }}</h2>
					<p>{{ t('dashboard_geo.subtitle', { aiType: aiTypeName }) }}</p>
				</div>
				<Card v-skeleton="{ show: loading }" style="border-radius: 8px;">
					<spiderGraphsAnalysis analysis-type="labels" :max-selections="6" />
				</Card>
			</template>

			<template id="geo_analysis" v-if="pageView === 'geo_analysis'">
				<div class="title">
					<h2>{{ t('dashboard_geo.key_benefit.title') }}</h2>
					<p>{{ t('dashboard_geo.subtitle', { aiType: aiTypeName }) }}</p>
				</div>
				<Card v-skeleton="{ show: loading }" style="border-radius: 8px;">
					<keyBenefitAnalysis />
				</Card>
			</template>

			<template id="task_management" v-else-if="pageView === 'task_management'">
				<section id="projectManagement">
					<suspense>
						<taskManagement />
						<template #fallback>
							<taskManagementSkeleton />
						</template>
					</suspense>
				</section>
			</template>
		</main>
	</featureSection>
</template>

<script setup>
import { ref, watch, watchEffect, computed, onMounted, onBeforeMount, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import calendar from '../../components/dashboard/calendar.vue';
import overviewKPISkeleton from '../../components/dashboard/overviewKPISkeleton.vue';
import overviewGeo from '../../components/dashboard/overviewGeo.vue';
import taskManagementSkeleton from '../../components/visibility/kpis/averagePositionSkeletton.vue';
import taskManagement from '../../components/dashboard/taskManagement.vue';
import InputSwitch from '@/components/utilities/forms/InputSwitch.vue';
import featureSection from '@/components/utilities/featureSection.vue';
import competitorsMenu from '@/components/visibility/competitorsMenu.vue';
import { useRouter, useRoute } from 'vue-router';
import Card from '@/components/simples/Card.vue';
import graphShareVoice from '@/components/dashboard/graphShareVoice.vue';
import competitiveGeoAnalysis from '@/components/dashboard/competitiveGeoAnalysis.vue';
import brandPerformance from '@/components/dashboard/brandPerformance.vue';
import spiderGraphsAnalysis from '@/components/dashboard/SpiderGraphsAnalysis.vue';
import keyBenefitAnalysis from '@/components/dashboard/KeyBenefitAnalysis.vue';
import Skeleton from 'primevue/skeleton';
import loadDashboardGeo from '@/components/utilities/loads/loadDashboardGeo.vue';
import dashboardGeoCalendar from '@/components/dashboard/dashboardGeoCalendar.vue';

import { useSubNavigationStore } from '@/store/global/subNavigation';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useDashboardStore } from '@/store/dashboard/dashboardStore.js';
import { useAuthStore } from '@/store/global/auth';

const subNavigationStore = useSubNavigationStore();
const { selectedAiTypeDashboard, aiTypeOptionsDashboard } = storeToRefs(subNavigationStore);

const dashboardGeoStore = useDashboardGeoStore();
const { loading, scores, analysis, sentiments, labels, selectedLabelIds, labelQuestions } = storeToRefs(dashboardGeoStore);

const router = useRouter();
const route = useRoute();
// i18n
import { useI18n } from 'vue-i18n';

const { t, locale, availableLocales } = useI18n();

const dashboardStore = useDashboardStore();
let ga4WithFilters = dashboardStore.filter;

let localAuth = localStorage.getItem('auth');

if (!localAuth) {
	window.location.href = '/login';
}

const user = useAuthStore();
const selected_dates = JSON.parse(localStorage.getItem('dashboardSelectedDates'));

const aiTypeName = computed(() => {
	const selected = aiTypeOptionsDashboard.value.find(
		option => option.id === selectedAiTypeDashboard.value
	);
	return selected ? selected.name : '';
});

const labelDetailsOpen = ref(false);

const pageView = ref('geo_analysis');

const changePageView = view => {
	pageView.value = view;
	let newPath = view === 'task_management' ? '/team-management' : '/geo-analysis';
	if (newPath !== route.path) {
		router.push({ path: newPath });
	}
};

const compared_dates = computed(() => {
	if (selected_dates) {
		const startDate = selected_dates['from'].replaceAll('-', '/');
		const endDate = selected_dates['to'].replaceAll('-', '/');

		return `${t('dashboard.from')} ${startDate} ${t('dashboard.to')} ${endDate}`;
	} else {
		const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
		const endDate = new Date(new Date().setDate(startDate.getDate() + 7));
		let date_from = startDate.toISOString().substring(0, 10).split('-');
		let date_to = endDate.toISOString().substring(0, 10).split('-');
		return `${t('dashboard.from')} ${date_from[2]}-${date_from[1]}-${date_from[0]} ${t('dashboard.to')} ${date_to[2]}-${date_to[1]}-${date_to[0]}`;
	}
});

const setManagementDate = value => {
	const to = new Date();
	let from = new Date();

	switch (value) {
		case '1W':
			from.setDate(from.getDate() - 7);
			break;
		case '1M':
			from.setMonth(from.getMonth() - 1);
			break;
		case '6M':
			from.setMonth(from.getMonth() - 6);
			break;
		case '1Y':
			from.setFullYear(from.getFullYear() - 1);
			break;
		case 'all':
			from = null;
			break;
		default:
			break;
	}

	localStorage.setItem('dashboardManagementSelectedDates', JSON.stringify({ from: from !== null ? from.toISOString().substring(0, 10) : null, to: to.toISOString().substring(0, 10) }));
};

if (!localStorage.getItem('dashboardManagementSelectedDates')) {
	setManagementDate('all');
}

onBeforeMount(async () => {
	let dbLanguage = user.user.language;
	let loginLanguage = localStorage.getItem('language');

	try {
		if (availableLocales.includes(dbLanguage)) {
			locale.value = dbLanguage;
		} else {
			locale.value = 'en';
		}
	} catch (e) {
		console.error('Error during language initialization:', e);
		locale.value = 'en';
	}
});


watch(
	() => route.path, newPath => {
		pageView.value = newPath === '/team-management' ? 'task_management' : 'geo_analysis';
	},
	{ immediate: true },
);

// await dashboardStore.fetchAllGeoData(selectedMarket.value);
</script>

<style lang="scss" scoped>
.title {
	width: 100%;
	display: flex;
	flex-direction: column;
	margin: 2rem 0 1rem 0;
	gap: 0;

	h1 {
		font-size: 1.7rem;
		font-weight: 500;
	}

	h2 {
		font-size: 1.4rem;
		font-weight: 500;
	}

	p {
		font-size: 0.85rem;
		font-weight: 500;
		font-style: italic;
		color: var(--gris-400);
		line-height: 2.3rem;
	}
}

.dropdown-selected {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	background-color: var(--blanc);
	padding: 10px;
	border: 1px solid;
	border-radius: 4px;
	color: var(--noir);
	font-weight: 500;
	font-size: 0.9rem;
}

.filter {
	display: flex;
	justify-content: space-between;
	padding-bottom: 1.5rem;
	align-items: center;
	margin: 2.5rem 0 0 0;
	// background-color: var(--beige-100);
	padding: 0 1rem;
	width: 102%;
	z-index: 999;
	margin: 2.5rem -1rem 0 -1rem;
	transition: height 0.3s ease, padding 0.3s ease, transform 0.3s ease;

	&.filters-shrunk {
		background-color: var(--beige-100);
		transform: scaleY(0.95);
		padding: 1rem;
	}

	.search {
		display: flex;
		gap: 1rem;
		align-items: center;

		input {
			width: 20rem;
			height: 2rem;
			border: 1px solid var(--gris-400);
			border-radius: 0.5rem;
			padding: 0 1rem;
			font-size: 0.85rem;
			color: var(--gris-800);
		}

		button {
			width: 6rem;
			height: 2rem;
			border: 1px solid var(--gris-400);
			border-radius: 0.5rem;
			background-color: var(--blanc);
			font-size: 0.85rem;
			color: var(--gris-400);
			cursor: pointer;

			&.yellow {
				background-color: var(--jaune-500);
				color: var(--noir);

				&:active {
					background-color: var(--jaune-600);
					color: var(--noir);
				}
			}

			&.blue {
				background-color: var(--bleu-200);
				color: var(--noir);

				&:active {
					background-color: var(--bleu-400);
					color: var(--noir);
				}
			}

			&:active {
				background-color: var(--gris-400);
				color: var(--blanc);
			}
		}
	}
}

.label-details-button {
	background-color: var(--bleu-200);
	border: none;
	padding: 0.5rem 0.8rem;
	border-radius: 6px;
	cursor: pointer;
	font-weight: 500;
}

.label-details-panel {
	margin-top: 1rem;
}

.label-details-list {
	margin: 0.5rem 0 0 1rem;
}

@media screen and (max-width: 1400px) {
	.search {
		margin-top: 3rem;
	}

	.title {
		margin-top: 1rem;
		margin-bottom: -2rem;
	}

	.calendar {
		flex-direction: column;
	}
}

@media screen and (max-width: 1370px) {
	.title {
		margin-top: 1rem;
		margin-bottom: 0rem;
		display: flex;
		justify-content: space-between;
	}

	.filter[data-v-ed7dad95] {
		display: flex;
		justify-content: space-between;
	}

	.filter {
		flex-direction: row;
		margin: 0.5rem 0 0 0;

		.calendar {
			//display: flex;
			flex-direction: row;
			justify-content: end;
			//max-width: 578px;
			margin: 0 0 0 0;

			//div {
			//  display: flex;
			//  align-items: start;
			//  gap: 0.5rem;
			//  margin-right: 5px;
			//}
		}
	}

	#kpis {
		flex-wrap: wrap;

		& :nth-child(n + 1) {
			span {
				padding-bottom: 1rem;
			}
		}
	}
}

.active-view {
	background-color: var(--jaune-500);
}

.view-option {
	cursor: pointer;
	transition: background-color 0.3s;
}

.view-option:not(.active-view):hover {
	background-color: var(--gris-100);
}
</style>
